<script>
import {projectState} from '@app/store/projectStore.svelte.js';
import {
    fontState, updateFontState
} from '@app/store/fontStore.svelte.js';
import {
    insertHistoryAction
} from '@app/store/historyStore.svelte.js';
import {
    v4 as uuidv4
} from 'uuid';
import { onMount } from 'svelte';
import {
	fade
} from 'svelte/transition';
import * as m from "@app/paraglide/messages.js";

let fontName = $state("");
let fontInputFocus;

onMount(() => {
    if (fontInputFocus) {
        fontInputFocus.focus();
    }
});

function addFont() {
    if (!fontName.trim()) return;
    
    let newStyleTag = document.createElement('style');
    newStyleTag.appendChild(document.createTextNode(`
        @font-face {
            font-family: ${fontName};
            src: local('${fontName}');
        }`
    ));

    document.head.appendChild(newStyleTag);
    
    // Create a new font array and use updateFontState instead of direct assignment
    updateFontState([...fontState, {
        id: uuidv4(),
        name: fontName
    }]);

    insertHistoryAction({
        name: "insert font", //action name
        eventGroup: projectState.selected,
        snapshots: [{
            store: "fontState",
            value: JSON.stringify(fontState)
        }]
    });
    
    fontName = "";
    
    // Auto-focus back to the input field
    if (fontInputFocus) {
        setTimeout(() => fontInputFocus.focus(), 0);
    }
}

function removeFont(id) {
    // Create a filtered font array and use updateFontState
    const filteredFonts = fontState.filter(font => id !== font.id);
    updateFontState(filteredFonts);

    insertHistoryAction({
        name: "remove font",
        eventGroup: projectState.selected,
        snapshots: [{
            store: "fontState",
            value: JSON.stringify(fontState)
        }]
    });
}

// Handle pressing Enter in the input field
function handleKeydown(event) {
    if (event.key === 'Enter' && fontName.trim()) {
        addFont();
    }
}
</script>

<div class="fonts-container">
    <div class="add-font-card">
        <div class="add-font-header">
            <h5>{m.options_fonts_add_font_title()}</h5>
            <p class="text-muted mb-3">{m.options_fonts_add_font_desc()}</p>
        </div>
        <div class="add-font-form">
            <div class="input-group mb-2">
                <input 
                    type="text" 
                    class="form-control" 
                    placeholder={m.options_fonts_font_name_placeholder()} 
                    bind:value={fontName}
                    bind:this={fontInputFocus}
                    onkeydown={handleKeydown}
                    id="newFontNameInput"
                    aria-label={m.options_fonts_new_font_name_label()}
                >
                <button 
                    class="btn btn-primary" 
                    type="button" 
                    title={m.options_fonts_add_font_button()} 
                    onclick={addFont} 
                    disabled={!fontName.trim()}
                    id="insertFontButton"
                    aria-label={m.options_fonts_add_font_button_label()}
                >
                    <i class="bi bi-plus-lg"></i> {m.options_fonts_add_font_button()}
                </button>
            </div>
        </div>
    </div>

    {#if fontState.length > 0}
        <div class="font-list-card">
            <div class="font-list-header">
                <h5>{m.options_fonts_available_fonts()}</h5>
            </div>
            <div class="font-list">
                {#each fontState as font (font.id)}
                    <div class="font-item" transition:fade={{ duration: 150 }}>
                        <div class="font-content">
                            <div class="font-name">
                                <span class="font-label">{font.name}</span>
                            </div>
                            <div class="font-preview" style="font-family: {font.name};">
                                {m.options_fonts_preview_sentence()}
                            </div>
                        </div>
                        <button 
                            class="btn btn-sm btn-icon" 
                            type="button" 
                            title={m.options_fonts_remove_font()} 
                            onclick={() => removeFont(font.id)} 
                            aria-label={m.options_fonts_remove_font_label()}
                        >
                            <i class="bi bi-trash"></i>
                        </button>
                    </div>
                {/each}
            </div>
        </div>
    {:else}
        <div class="empty-fonts">
            <div class="empty-icon">
                <i class="bi bi-fonts"></i>
            </div>
            <p>{m.options_fonts_empty_state()}</p>
        </div>
    {/if}
</div>

<style>
    .fonts-container {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .add-font-card,
    .font-list-card {
        background-color: var(--bs-light);
        border-radius: 0.5rem;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    }

    .add-font-header,
    .font-list-header {
        padding: 1rem;
        border-bottom: 1px solid rgba(0,0,0,0.1);
    }

    .add-font-header h5,
    .font-list-header h5 {
        margin-bottom: 0.25rem;
        font-weight: 600;
    }

    .add-font-form {
        padding: 1rem;
    }

    .font-list {
        max-height: 400px;
        overflow-y: auto;
    }

    .font-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem;
        border-bottom: 1px solid rgba(0,0,0,0.05);
        transition: background-color 0.15s ease;
    }

    .font-item:hover {
        background-color: rgba(0,0,0,0.02);
    }

    .font-content {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        gap: 0.5rem;
    }

    .font-name {
        display: flex;
        align-items: center;
    }

    .font-label {
        font-size: 0.95rem;
        font-weight: 600;
    }

    .font-preview {
        font-size: 1rem;
        color: var(--bs-dark);
    }

    .btn-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        color: var(--bs-danger);
        background: transparent;
        transition: background-color 0.15s ease;
    }

    .btn-icon:hover {
        background-color: rgba(220, 53, 69, 0.1);
    }

    .empty-fonts {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        text-align: center;
        background-color: var(--bs-light);
        border-radius: 0.5rem;
        color: var(--bs-secondary);
    }

    .empty-icon {
        font-size: 2rem;
        margin-bottom: 1rem;
        opacity: 0.6;
    }
</style>
